export type ShiftHandoverLike = {
  id?: string | null;
  facilityId?: string | null;
  handoverDate?: string | null;
  urgencyLevel?: string | null;
  acknowledged?: boolean | null;
  acknowledgedAt?: string | null;
  signedOffAt?: string | null;
  toStaffId?: string | null;
  priorityClients?: unknown[] | null;
};

export interface ShiftHandoverContinuitySummary {
  total: number;
  acknowledgedCount: number;
  pendingAcknowledgementCount: number;
  signedOffCount: number;
  pendingSignOffCount: number;
  urgentPendingSignOffCount: number;
  urgentPendingCount: number;
  priorityPendingCount: number;
  unassignedIncomingCount: number;
  priorityClientNoteCount: number;
  missingFacilityCount: number;
  latestHandoverDate: string | null;
  oldestPendingDate: string | null;
}

export function summariseShiftHandoverContinuity(
  handovers: readonly ShiftHandoverLike[],
): ShiftHandoverContinuitySummary {
  let acknowledgedCount = 0;
  let pendingAcknowledgementCount = 0;
  let signedOffCount = 0;
  let pendingSignOffCount = 0;
  let urgentPendingSignOffCount = 0;
  let urgentPendingCount = 0;
  let priorityPendingCount = 0;
  let unassignedIncomingCount = 0;
  let priorityClientNoteCount = 0;
  let missingFacilityCount = 0;
  let latestHandoverDate: string | null = null;
  let oldestPendingDate: string | null = null;

  for (const handover of handovers) {
    const handoverDate = handover.handoverDate || null;
    const urgency = (handover.urgencyLevel || 'ROUTINE').toUpperCase();
    const acknowledged = handover.acknowledged === true;
    const signedOff = Boolean(handover.signedOffAt);

    if (acknowledged) {
      acknowledgedCount += 1;
    } else {
      pendingAcknowledgementCount += 1;
      if (urgency === 'URGENT') urgentPendingCount += 1;
      if (urgency === 'PRIORITY') priorityPendingCount += 1;
      if (!oldestPendingDate || (handoverDate && handoverDate < oldestPendingDate)) {
        oldestPendingDate = handoverDate;
      }
    }

    if (signedOff) {
      signedOffCount += 1;
    } else if (acknowledged) {
      pendingSignOffCount += 1;
      if (urgency === 'URGENT') urgentPendingSignOffCount += 1;
    }

    if (!handover.toStaffId) unassignedIncomingCount += 1;
    if (!handover.facilityId) missingFacilityCount += 1;
    priorityClientNoteCount += Array.isArray(handover.priorityClients)
      ? handover.priorityClients.length
      : 0;

    if (!latestHandoverDate || (handoverDate && handoverDate > latestHandoverDate)) {
      latestHandoverDate = handoverDate;
    }
  }

  return {
    total: handovers.length,
    acknowledgedCount,
    pendingAcknowledgementCount,
    signedOffCount,
    pendingSignOffCount,
    urgentPendingSignOffCount,
    urgentPendingCount,
    priorityPendingCount,
    unassignedIncomingCount,
    priorityClientNoteCount,
    missingFacilityCount,
    latestHandoverDate,
    oldestPendingDate,
  };
}

export type PendingSyncOperationLike = {
  type?: string | null;
  payload?: Record<string, unknown> | null;
};

export interface PendingSyncSummary {
  total: number;
  criticalCount: number;
  handoverCreateCount: number;
  unassignedHandoverEscalationCount: number;
  urgentHandoverCount: number;
  incidentCount: number;
  medicationCount: number;
  clockCount: number;
}

const CRITICAL_SYNC_TYPES = new Set([
  'SIRS_INCIDENT',
  'MED_ADMIN_S8',
  'INCIDENT_CREATE',
  'RESTRICTIVE_PRACTICE_CREATE',
  'WHS_INCIDENT_CREATE',
]);

const INCIDENT_SYNC_TYPES = new Set([
  'INCIDENT_CREATE',
  'SIRS_INCIDENT',
  'WHS_INCIDENT_CREATE',
  'RESTRICTIVE_PRACTICE_CREATE',
]);

const MEDICATION_SYNC_TYPES = new Set(['MED_ADMIN', 'MED_ADMIN_S8']);
const CLOCK_SYNC_TYPES = new Set(['CLOCK_IN', 'CLOCK_OUT']);

export function summarisePendingSyncOperations(
  operations: readonly PendingSyncOperationLike[],
): PendingSyncSummary {
  let criticalCount = 0;
  let handoverCreateCount = 0;
  let unassignedHandoverEscalationCount = 0;
  let urgentHandoverCount = 0;
  let incidentCount = 0;
  let medicationCount = 0;
  let clockCount = 0;

  for (const operation of operations) {
    const type = (operation.type || '').toUpperCase();
    if (!type) continue;

    if (CRITICAL_SYNC_TYPES.has(type)) criticalCount += 1;
    if (INCIDENT_SYNC_TYPES.has(type)) incidentCount += 1;
    if (MEDICATION_SYNC_TYPES.has(type)) medicationCount += 1;
    if (CLOCK_SYNC_TYPES.has(type)) clockCount += 1;

    if (type === 'HANDOVER_CREATE') {
      handoverCreateCount += 1;
      if (operation.payload?.recipientMode === 'ESCALATE_UNASSIGNED') {
        unassignedHandoverEscalationCount += 1;
      }
      const urgency =
        typeof operation.payload?.urgencyLevel === 'string'
          ? operation.payload.urgencyLevel.toUpperCase()
          : 'ROUTINE';
      if (urgency === 'URGENT') urgentHandoverCount += 1;
    }
  }

  return {
    total: operations.length,
    criticalCount,
    handoverCreateCount,
    unassignedHandoverEscalationCount,
    urgentHandoverCount,
    incidentCount,
    medicationCount,
    clockCount,
  };
}
